import { createSelector } from 'reselect';
import { AppState } from 'store';
import { ClientCredentialsState } from './reducer';

// Base selector to get the clientCredentials slice from the root state
const selectClientCredentialsState = (state: AppState): ClientCredentialsState => state.clientCredentialsState;

// Selector to get all client credentials (paginated)
export const selectAllClientCredentials = createSelector(
  selectClientCredentialsState,
  (credentialsState) => credentialsState.credentials,
);

// Selector to get the list of credential data
export const selectClientCredentialsData = createSelector(selectAllClientCredentials, (credentials) =>
  credentials ? credentials.Data : [],
);

// Selector to get pagination info
export const selectClientCredentialsPagination = createSelector(selectAllClientCredentials, (credentials) =>
  credentials ? credentials.Pagination : null,
);

// Selector to get the currently selected client credential
export const selectCurrentClientCredential = createSelector(
  selectClientCredentialsState,
  (credentialsState) => credentialsState.currentCredential,
);

// Selector to get newly created credential (with client_id and client_secret)
export const selectNewlyCreatedCredential = createSelector(
  selectClientCredentialsState,
  (credentialsState) => credentialsState.newlyCreatedCredential,
);

// Selector to get rotated credential (with new client_secret)
export const selectRotatedCredential = createSelector(
  selectClientCredentialsState,
  (credentialsState) => credentialsState.rotatedCredential,
);

// Selector to get loading status
export const selectClientCredentialsLoading = createSelector(
  selectClientCredentialsState,
  (credentialsState) => credentialsState.loading,
);

// Selector to get any error from the client credentials operations
export const selectClientCredentialsError = createSelector(
  selectClientCredentialsState,
  (credentialsState) => credentialsState.error,
);

// Selector to get active (non-deleted) credentials
export const selectActiveClientCredentials = createSelector(selectClientCredentialsData, (credentials) =>
  credentials.filter((cred) => !cred.isDeleted),
);

// Selector to get a specific credential by ID
export const selectClientCredentialById = (credentialId: string) =>
  createSelector(selectClientCredentialsData, (credentials) => credentials.find((cred) => cred.id === credentialId));

// Selector to get a specific credential by client ID
export const selectClientCredentialByClientId = (clientId: string) =>
  createSelector(selectClientCredentialsData, (credentials) => credentials.find((cred) => cred.clientID === clientId));
